import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleViewDetails = () => {
    onViewDetails(project);
  };

  return (
    <div 
      className="bg-card rounded-xl border border-border overflow-hidden brand-shadow hover:brand-shadow-lg transition-all duration-300 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={project?.image}
          alt={project?.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Project Status Badge */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-brand-medium ${
            project?.status === 'Live' ?'bg-success text-success-foreground' 
              : project?.status === 'In Development' ?'bg-warning text-warning-foreground' :'bg-accent text-accent-foreground'
          }`}>
            {project?.status}
          </span>
        </div>

        {/* Quick Actions */}
        <div className={`absolute top-4 right-4 flex space-x-2 transition-all duration-300 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}>
          {project?.liveUrl && (
            <a
              href={project?.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-background/90 backdrop-blur-sm rounded-lg text-text-primary hover:text-primary transition-brand"
              aria-label="View live project"
            >
              <Icon name="ExternalLink" size={16} />
            </a>
          )}
          {project?.githubUrl && (
            <a
              href={project?.githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-background/90 backdrop-blur-sm rounded-lg text-text-primary hover:text-primary transition-brand"
              aria-label="View source code"
            >
              <Icon name="Github" size={16} />
            </a>
          )}
        </div>
      </div>
      {/* Project Content */}
      <div className="p-6">
        {/* Project Header */}
        <div className="mb-4">
          <h3 className="text-xl font-brand-subheading text-foreground mb-2 group-hover:text-primary transition-brand">
            {project?.title}
          </h3>
          {project?.role && (
            <span className="inline-block px-2 py-0.5 mb-2 bg-accent/10 text-accent text-xs font-brand-medium rounded-md">
              {project.role}
            </span>
          )}
          <p className="text-text-secondary text-sm font-brand-body line-clamp-2">
            {project?.description}
          </p>
        </div>

        {/* Technology Stack */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {project?.technologies?.slice(0, 4)?.map((tech, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-primary/10 text-primary text-xs font-brand-medium rounded-md border border-primary/20"
              >
                {tech}
              </span>
            ))}
            {project?.technologies?.length > 4 && (
              <span className="px-2 py-1 bg-muted text-text-secondary text-xs font-brand-medium rounded-md">
                +{project?.technologies?.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Project Metrics */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-4">
            {project?.metrics?.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-lg font-brand-subheading text-primary">
                  {metric?.value}
                </div>
                <div className="text-xs text-text-secondary font-brand-body">
                  {metric?.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="default"
            size="sm"
            onClick={handleViewDetails}
            iconName="Eye"
            iconPosition="left"
            iconSize={16}
            className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            View Details
          </Button>
          {project?.liveUrl && (
            <Button
              variant="outline"
              size="sm"
              asChild
              iconName="ExternalLink"
              iconPosition="left"
              iconSize={16}
              className="border-primary/20 text-primary hover:bg-primary/10"
            >
              <a href={project?.liveUrl} target="_blank" rel="noopener noreferrer">
                Live Demo
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;